import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, CircleDot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { useOrganization } from "../../contexts/OrganizationContext";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { RenamedFolderFinding } from "../../models/RenamedFolderFinding";
import RenamedFoldersRepository from "../../repositories/RenamedFoldersRepository";
import { FilterCombobox } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import TableEmptyState from "../common/TableEmptyState";
import TablePagination from "../common/Table/TablePagination";
import { SearchInput } from "../common/Table/SearchInput";
import { useTableHandlers } from "../common/Table/Hooks/UseTableHandlers";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import MigrationEntityCell from "./MigrationEntityCell";

const entityTypeOptions = [
  MigrationType.CasesMigration,
  MigrationType.IntakesMigration,
  MigrationType.ProvidersMigration,
].map((type) => ({ value: type, label: MigrationTypeLabels[type] }));

const statusOptions = [
  { value: "unresolved", label: "Still diverged" },
  { value: "resolved", label: "Absorbed" },
];

export const RenamedFoldersTable: React.FC = () => {
  const repository = new RenamedFoldersRepository();
  const { selectedOrganizationId } = useOrganization();

  const [entityTypeFilter, setEntityTypeFilter] = useState<MigrationType | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [findings, setFindings] = useState<RenamedFolderFinding[]>([]);

  const { order, page, rowsPerPage, rows, changeOrder, handleRowsPerPageChange, setPage, setRows } =
    useTableHandlers({ order: "desc", sort: "lastReportedOn", page: 0, rowsPerPage: 50 });

  const unresolved = statusFilter === null ? null : statusFilter === "unresolved";

  const findingsQuery = useQuery({
    queryKey: [
      QUERY_KEYS.RENAMED_FOLDER_FINDINGS,
      selectedOrganizationId,
      entityTypeFilter,
      statusFilter,
      searchQuery,
      page,
      rowsPerPage,
      order,
    ],
    queryFn: () =>
      repository.getRenamedFolderFindings({
        entityType: entityTypeFilter,
        unresolved,
        search: searchQuery || null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        descending: order === "desc",
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
    enabled: !!selectedOrganizationId,
  });

  useEffect(() => {
    if (findingsQuery.data) {
      setFindings(findingsQuery.data.data);
      setRows(findingsQuery.data.paging?.totalCount ?? findingsQuery.data.data.length);
    }
  }, [findingsQuery.data, setRows]);

  const loading = findingsQuery.isFetching || findingsQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <div className={filterSearchClassName}>
          <SearchInput
            label="Search"
            placeholder="Stored or live folder name"
            onSearch={(value) => {
              setSearchQuery(value);
              setPage(0);
            }}
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={entityTypeFilter}
            onChange={(value) => {
              setEntityTypeFilter(value as MigrationType | null);
              setPage(0);
            }}
            options={entityTypeOptions}
            placeholder="All types"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(0);
            }}
            options={statusOptions}
            placeholder="All findings"
          />
        </div>
      </div>

      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[24%]">Entity</TableHead>
              <TableHead className="w-[46%]">Stored name &rarr; live name</TableHead>
              <TableHead className="w-[14%]">Status</TableHead>
              <TableHead className="w-[16%]">
                <SortableTableColumnHeader
                  label="Last seen"
                  sortProperty="lastReportedOn"
                  getSort={() => "lastReportedOn"}
                  setSort={() => undefined}
                  getOrder={() => order}
                  setOrder={(next) => changeOrder(next)}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {findings.map((finding) => (
              <TableRow key={finding.id}>
                <TableCell>
                  <MigrationEntityCell
                    entityType={finding.entityType}
                    migrationEntityId={finding.migrationEntityId}
                    entityLabel={finding.entityLabel}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate text-muted-foreground">{finding.storedName}</span>
                    <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium">{finding.liveName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {finding.resolvedOn ? (
                    <Badge variant="success">
                      <CheckCircle2 className="size-3" />
                      Absorbed
                    </Badge>
                  ) : (
                    <Badge variant="warning">
                      <CircleDot className="size-3" />
                      Diverged
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{convertUtcToLocal(finding.lastReportedOn)}</TableCell>
              </TableRow>
            ))}
            {findings.length === 0 && !loading && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    title="No renamed folders recorded"
                    description="Folders renamed in Neos appear here once a client has swept this organization."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default RenamedFoldersTable;
